import React, { useMemo, useState, useRef } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Save, Plus, X, CheckCircle2, Circle, Upload, Paperclip, Download, Bell, FolderOpen, Sparkles } from 'lucide-react';
import { BriefingImportDialog } from './BriefingImportDialog';
import { toast } from 'sonner';
import { BRIEFING_SECTIONS, BRIEFING_STATUS } from '../lib/briefingConfig';

const sectionHasData = (sectionData) => {
  if (!sectionData) return false;
  return Object.values(sectionData).some((v) => {
    if (Array.isArray(v)) return v.length > 0 && v.some((x) => (typeof x === 'object' ? Object.values(x).some(Boolean) : Boolean(x)));
    return Boolean(v) && String(v).trim() !== '';
  });
};

/**
 * Reusable briefing form. Used by both the client portal and the agency ficha.
 * Props:
 *  - data: object keyed by section -> { fieldKey: value }
 *  - onSaveSection: async (sectionKey, sectionData) => void
 *  - readOnly: boolean (default false)
 */
export const BriefingForm = ({ data = {}, onSaveSection, onUploadFile, buildFileUrl, readOnly = false, status, onSetStatus, onRemind, onExtractFromFile, hiddenSections = [], paymentMethod = '' }) => {
  const SECTIONS = useMemo(() => BRIEFING_SECTIONS.filter((s) => !hiddenSections.includes(s.key)), [hiddenSections.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const [activeKey, setActiveKey] = useState(SECTIONS[0].key);
  const [formData, setFormData] = useState(() => JSON.parse(JSON.stringify(data || {})));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});
  const [statusBusy, setStatusBusy] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInputRef = useRef(null);

  // Importar briefing desde archivo (Notion .md/.zip, .docx, .pdf) → propuesta, nunca guarda directo
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onExtractFromFile) return;
    setImporting(true);
    try {
      const schema = SECTIONS.map((sec) => ({ key: sec.key, title: sec.title, fields: sec.fields.map((f) => ({ key: f.key, label: f.label, type: f.type, options: f.options, columns: f.columns })) }));
      const res = await onExtractFromFile(file, schema);
      setImportResult(res);
    } catch (err) {
      toast.error(err?.message || 'No se pudo analizar el archivo');
    } finally {
      setImporting(false);
    }
  };

  const applyImport = async (bySection) => {
    const sections = Object.keys(bySection);
    let next = formData;
    for (const key of sections) {
      next = { ...next, [key]: { ...(next[key] || {}), ...bySection[key] } };
      await onSaveSection(key, next[key]);
    }
    setFormData(next);
    toast.success(`Briefing actualizado: ${sections.length} ${sections.length === 1 ? 'sección' : 'secciones'}`);
  };

  const activeSection = useMemo(() => SECTIONS.find((s) => s.key === activeKey), [activeKey, SECTIONS]);

  const getVal = (fieldKey) => formData[activeKey]?.[fieldKey];

  const statusMeta = BRIEFING_STATUS[status] || BRIEFING_STATUS.pendiente;

  const doSetStatus = async (s) => {
    if (!onSetStatus) return;
    setStatusBusy(true);
    try { await onSetStatus(s); } finally { setStatusBusy(false); }
  };

  const handleRemind = async () => {
    if (!onRemind) return;
    setReminding(true);
    try { await onRemind(); } finally { setReminding(false); }
  };

  const setVal = (fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [activeKey]: { ...(prev[activeKey] || {}), [fieldKey]: value },
    }));
    setDirty((d) => ({ ...d, [activeKey]: true }));
  };

  // Persist a single field immediately (used for file uploads so they never get lost)
  const persistFieldImmediate = async (fieldKey, value) => {
    const nextSection = { ...(formData[activeKey] || {}), [fieldKey]: value };
    setFormData((prev) => ({ ...prev, [activeKey]: nextSection }));
    try {
      await onSaveSection(activeKey, nextSection);
    } catch (e) {
      toast.error('Error al guardar el archivo');
    }
  };

  const saveSection = async (key = activeKey) => {
    if (readOnly) return;
    setSaving(true);
    try {
      await onSaveSection(key, formData[key] || {});
      setDirty((d) => ({ ...d, [key]: false }));
      toast.success('Sección guardada');
    } catch (e) {
      toast.error(e?.message || 'Error al guardar la sección');
    } finally {
      setSaving(false);
    }
  };

  const switchSection = async (key) => {
    if (key === activeKey) return;
    if (!readOnly && dirty[activeKey]) {
      await saveSection(activeKey);
    }
    setActiveKey(key);
  };

  const completed = SECTIONS.filter((s) => sectionHasData(formData[s.key])).length;

  return (
    <div>
      {(onSetStatus || onRemind || (!readOnly && onExtractFromFile)) && (
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4 pb-4 border-b" data-testid="briefing-status-bar">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Estado del briefing:</span>
            <Badge className={statusMeta.color} variant="outline" data-testid="briefing-status">{statusMeta.label}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && onExtractFromFile && (
              <>
                <input ref={importInputRef} type="file" accept=".pdf,.docx,.md,.txt,.zip" className="hidden" onChange={handleImportFile} data-testid="briefing-import-input" />
                <Button variant="outline" size="sm" onClick={() => importInputRef.current?.click()} disabled={importing} title="PDF, Word (.docx), Markdown/TXT o .zip exportado de Notion" data-testid="briefing-import-btn">
                  {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  {importing ? 'Analizando…' : 'Importar briefing desde archivo'}
                </Button>
              </>
            )}
            {onRemind && (
              <Button variant="outline" size="sm" onClick={handleRemind} disabled={reminding} data-testid="briefing-remind-btn">
                {reminding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />}
                Recordar al cliente
              </Button>
            )}
            {onSetStatus && status !== 'completado' && (
              <Button size="sm" onClick={() => doSetStatus('completado')} disabled={statusBusy} data-testid="briefing-complete-btn">
                {statusBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Marcar como completado
              </Button>
            )}
            {onSetStatus && status === 'completado' && (
              <Button variant="outline" size="sm" onClick={() => doSetStatus('en_proceso')} disabled={statusBusy} data-testid="briefing-reopen-btn">
                {statusBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Reabrir briefing
              </Button>
            )}
          </div>
        </div>
      )}
      <BriefingImportDialog open={!!importResult} onClose={() => setImportResult(null)} result={importResult} currentData={formData} onApply={applyImport} />
      <div className="flex flex-col md:flex-row gap-4">
      {/* Section nav */}
      <div className="md:w-64 shrink-0">
        <div className="mb-3 text-xs text-muted-foreground">
          {completed}/{SECTIONS.length} secciones con datos
        </div>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
          {SECTIONS.map((s, idx) => {
            const has = sectionHasData(formData[s.key]);
            return (
              <button
                key={s.key}
                onClick={() => switchSection(s.key)}
                className={`w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg transition-colors ${activeKey === s.key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                data-testid={`briefing-nav-${s.key}`}
              >
                {has ? <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" /> : <Circle className="w-4 h-4 shrink-0 opacity-40" />}
                <span className="truncate">{idx + 1}. {s.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active section fields */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{activeSection.title}</h3>
          {!readOnly && (
            <Button onClick={() => saveSection()} disabled={saving} size="sm" data-testid="briefing-save-section">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar sección
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {activeSection.fields.map((field) => (
            <BriefingField
              key={field.key}
              field={field}
              value={getVal(field.key)}
              onChange={(v) => setVal(field.key, v)}
              onPersist={(v) => persistFieldImmediate(field.key, v)}
              onUploadFile={onUploadFile}
              buildFileUrl={buildFileUrl}
              readOnly={readOnly}
              paymentMethod={paymentMethod}
            />
          ))}
        </div>

        {!readOnly && (
          <div className="mt-6 flex justify-end">
            <Button onClick={() => saveSection()} disabled={saving} data-testid="briefing-save-section-bottom">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar sección
            </Button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

const PAYMENT_LABELS = { sepa: 'Domiciliación SEPA (recibo)', transferencia: 'Transferencia' };

const BriefingField = ({ field, value, onChange, onPersist, onUploadFile, buildFileUrl, readOnly, paymentMethod }) => {
  if (field.type === 'payment_link') {
    return (
      <div data-testid="briefing-forma-pago">
        <Label className="mb-1 block">{field.label}</Label>
        <p className="text-sm font-medium" data-testid="briefing-forma-pago-value">{PAYMENT_LABELS[paymentMethod] || 'Sin elegir todavía'}</p>
        <p className="text-xs text-muted-foreground">Se elige en la sección Bienvenida del portal. Aquí solo se muestra.</p>
      </div>
    );
  }
  if (field.type === 'info') {
    return (
      <div className="p-3 rounded-lg bg-muted/50 border text-sm text-muted-foreground">
        {field.label}
      </div>
    );
  }

  const label = <Label className="mb-1 block">{field.label}</Label>;

  if (field.type === 'files') {
    return <FilesField field={field} value={value} onPersist={onPersist} onUploadFile={onUploadFile} buildFileUrl={buildFileUrl} readOnly={readOnly} />;
  }

  if (readOnly) {
    // Read-only rendering
    if (field.type === 'drivelist') {
      const rows = Array.isArray(value) ? value : [];
      return (
        <div>{label}{rows.length ? (
          <ul className="space-y-1 text-sm">
            {rows.map((r, i) => (
              <li key={i} className="flex items-center gap-2">
                {r.compartida ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 opacity-40 shrink-0" />}
                {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">{r.nombre || r.url}</a> : <span className="truncate">{r.nombre || '—'}</span>}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-muted-foreground italic">Sin carpetas</p>}</div>
      );
    }
    if (field.type === 'list') {
      const items = Array.isArray(value) ? value : [];
      return (
        <div>{label}{items.length ? <ul className="list-disc pl-5 text-sm">{items.map((it, i) => <li key={i}>{it}</li>)}</ul> : <p className="text-sm text-muted-foreground italic">Sin respuesta</p>}</div>
      );
    }
    if (field.type === 'table') {
      const rows = Array.isArray(value) ? value : [];
      return (
        <div>{label}{rows.length ? (
          <div className="border rounded-lg overflow-hidden text-sm">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 p-2 border-b last:border-0">
                {field.columns.map((c) => <span key={c}>{row[c] || '—'}</span>)}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground italic">Sin respuesta</p>}</div>
      );
    }
    return (
      <div>{label}<p className="text-sm whitespace-pre-wrap">{value ? value : <span className="text-muted-foreground italic">Sin respuesta</span>}</p></div>
    );
  }

  switch (field.type) {
    case 'text':
      return <div>{label}<Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={`briefing-field-${field.key}`} /></div>;
    case 'textarea':
      return <div>{label}<Textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={`briefing-field-${field.key}`} /></div>;
    case 'select':
      return (
        <div>{label}
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger data-testid={`briefing-field-${field.key}`}><SelectValue placeholder="Selecciona..." /></SelectTrigger>
            <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    case 'list':
      return <ListField field={field} value={value} onChange={onChange} />;
    case 'table':
      return <TableField field={field} value={value} onChange={onChange} />;
    case 'drivelist':
      return <DriveListField field={field} value={value} onChange={onChange} />;
    default:
      return null;
  }
};

const ListField = ({ field, value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const update = (i, v) => { const next = [...items]; next[i] = v; onChange(next); };
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <Label className="mb-1 block">{field.label}</Label>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input value={it} onChange={(e) => update(i, e.target.value)} placeholder={field.placeholder} data-testid={`briefing-list-${field.key}-${i}`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}><X className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`briefing-list-add-${field.key}`}><Plus className="w-4 h-4 mr-1" />Añadir</Button>
      </div>
    </div>
  );
};

const TableField = ({ field, value, onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, col, v) => { const next = rows.map((r) => ({ ...r })); next[i] = { ...next[i], [col]: v }; onChange(next); };
  const add = () => onChange([...rows, Object.fromEntries(field.columns.map((c) => [c, '']))]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div>
      <Label className="mb-1 block">{field.label}</Label>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="grid grid-cols-3 gap-2 flex-1">
              {field.columns.map((c) => (
                <Input key={c} value={row[c] || ''} onChange={(e) => update(i, c, e.target.value)} placeholder={c} data-testid={`briefing-table-${field.key}-${i}-${c}`} />
              ))}
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}><X className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`briefing-table-add-${field.key}`}><Plus className="w-4 h-4 mr-1" />Añadir fila</Button>
      </div>
    </div>
  );
};

const DriveListField = ({ field, value, onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, k, v) => { const next = rows.map((r) => ({ ...r })); next[i] = { ...next[i], [k]: v }; onChange(next); };
  const add = () => onChange([...rows, { nombre: '', url: '', compartida: false }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div>
      <Label className="mb-1 block">{field.label}</Label>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground italic">Aún no hay carpetas. Añade las carpetas de Drive que vas a compartir.</p>}
        {rows.map((row, i) => (
          <div key={i} className="flex gap-2 items-center p-2 rounded-lg border bg-muted/20">
            <label className="flex items-center gap-1 shrink-0 cursor-pointer" title="Marcar como compartida">
              <input
                type="checkbox"
                checked={!!row.compartida}
                onChange={(e) => update(i, 'compartida', e.target.checked)}
                className="w-4 h-4 accent-emerald-600"
                data-testid={`briefing-drive-shared-${field.key}-${i}`}
              />
            </label>
            <div className="grid grid-cols-2 gap-2 flex-1">
              <Input value={row.nombre || ''} onChange={(e) => update(i, 'nombre', e.target.value)} placeholder="Nombre de la carpeta" data-testid={`briefing-drive-name-${field.key}-${i}`} />
              <Input value={row.url || ''} onChange={(e) => update(i, 'url', e.target.value)} placeholder="Enlace de Drive (opcional)" data-testid={`briefing-drive-url-${field.key}-${i}`} />
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}><X className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`briefing-drive-add-${field.key}`}><FolderOpen className="w-4 h-4 mr-1" />Añadir carpeta</Button>
      </div>
    </div>
  );
};

const FilesField = ({ field, value, onPersist, onUploadFile, buildFileUrl, readOnly }) => {
  const items = Array.isArray(value) ? value : [];
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const handleSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !onUploadFile) return;
    setUploading(true);
    try {
      let current = [...items];
      for (const f of files) {
        const meta = await onUploadFile(f);
        current = [...current, meta];
      }
      await onPersist(current);
      toast.success(files.length > 1 ? 'Archivos subidos' : 'Archivo subido');
    } catch (err) {
      toast.error(err?.message || 'Error subiendo el archivo');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (id) => {
    await onPersist(items.filter((it) => it.id !== id));
  };

  return (
    <div>
      <Label className="mb-1 block">{field.label}</Label>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-muted-foreground italic">Aún no hay archivos.</p>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border bg-muted/30 text-sm">
            <a href={buildFileUrl ? buildFileUrl(it.id) : '#'} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 text-primary hover:underline">
              <Paperclip className="w-4 h-4 shrink-0" />
              <span className="truncate">{it.name}</span>
              <Download className="w-3.5 h-3.5 shrink-0 opacity-60" />
            </a>
            {!readOnly && (
              <button onClick={() => remove(it.id)} className="text-red-500 hover:text-red-700 shrink-0" title="Eliminar archivo"><X className="w-4 h-4" /></button>
            )}
          </div>
        ))}
        {!readOnly && onUploadFile && (
          <>
            <input ref={inputRef} type="file" multiple className="hidden" onChange={handleSelect} data-testid={`briefing-file-input-${field.key}`} />
            <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`briefing-file-upload-${field.key}`}>
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Subir archivo
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default BriefingForm;